import {
  HHButton,
  HHCircularProgress,
  HHDialog,
  HHDialogActions,
  HHDialogContent,
  HHDialogTitle,
  HHTypography,
} from '@hinge-health/react-component-library';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SubmissionStatusEnum } from '../../../../../../types';
import { routes } from '../../../../constants/strings/routes';
import { useSnackbarContext } from '../../../../context/snackbar.context';
import { useClientInsurersById } from '../../../../hooks/client-insurer-hook';
import {
  BillingModelType,
  ContractTermSummary,
  PackageType,
  useGetContractTermSummaryLazyQuery,
} from '../../../../types';
import { formatCurrency } from '../../../../utils/currency-helpers';
import { BillManagementNavigator } from '../../../bills/bill-management/bill-management-navigator';
import { GridLevelName } from '../../../grid-components/grid-types';
import { ContractPackageFormValues } from '../custom-package-types';
import NewContractPackageDetails from './new-contract-package-details';
import { getConfirmationTitle } from './utils';

interface DateAdjustmentResult {
  adjustedStartDate: string;
  adjustedEndDate: string;
}

const isValidDateString = (dateString: string | null): dateString is string =>
  dateString !== null && dayjs(dateString).isValid();

const isDateBeforeToday = (dateString: string): boolean =>
  dayjs(dateString).isBefore(dayjs(), 'day');

const submissionStatusesBlockingContractVoid = [
  SubmissionStatusEnum.Accepted,
  SubmissionStatusEnum.Denied,
  SubmissionStatusEnum.Duplicate,
  SubmissionStatusEnum.Submitted,
  SubmissionStatusEnum.Unknown,
];

const adjustDates = (
  startDate: string,
  endDate: string | null,
  isVoid: boolean,
): DateAdjustmentResult => {
  const today = dayjs().format('YYYY-MM-DD');

  if (isVoid) {
    return {
      adjustedStartDate: startDate,
      adjustedEndDate: today,
    };
  }

  if (!endDate || !isValidDateString(endDate)) {
    return {
      adjustedStartDate: startDate,
      adjustedEndDate: today,
    };
  }

  if (isDateBeforeToday(endDate)) {
    return {
      adjustedStartDate: dayjs(endDate).add(1, 'day').format('YYYY-MM-DD'),
      adjustedEndDate: today,
    };
  }

  return {
    adjustedStartDate: startDate,
    adjustedEndDate: endDate,
  };
};

export interface PackageFormConfirmationProps {
  formValues: ContractPackageFormValues;
  billingModels: BillingModelType[];
  availablePackages: PackageType[];
  onAction: (action: 'cancel' | 'submit' | 'void' | 'edit') => void;
  editId?: number;
  open: boolean;
  isVoid?: boolean;
  confirmText?: string;
  isBatchCreate?: boolean;
}

export const PackageFormConfirmation = ({
  formValues,
  availablePackages,
  billingModels,
  onAction,
  editId,
  open,
  isVoid = false,
  confirmText = 'Confirm',
  isBatchCreate = false,
}: PackageFormConfirmationProps): JSX.Element => {
  const params = useParams<{ id: string }>();
  const clientInsurerId = parseInt(params.id ?? '0', 10);

  const { setSnackbarState } = useSnackbarContext();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string>(formValues.startDate);
  const [endDate, setEndDate] = useState<string | null>(formValues.endDate);
  const [subscriptionData, setSubscriptionData] =
    useState<ContractTermSummary | null>(null);

  const [getContractTermSummary, { error, loading }] =
    useGetContractTermSummaryLazyQuery();

  const {
    data: clientData,
    error: clientError,
    loading: clientLoading,
  } = useClientInsurersById(clientInsurerId);
  const clientId = clientData?.client.id;
  const insurerId = clientData?.insurer.id;

  useEffect(() => {
    if (isBatchCreate || !editId || !open) {
      return;
    }

    const currentStartDate = formValues.startDate;
    const currentEndDate = formValues.endDate;

    if (!isDateBeforeToday(currentStartDate)) {
      setSubscriptionData(null);
      return;
    }

    if (
      ((currentEndDate &&
        isValidDateString(currentEndDate) &&
        !isDateBeforeToday(currentEndDate)) ||
        !currentEndDate) &&
      !isVoid
    ) {
      setSubscriptionData(null);
      return;
    }

    const { adjustedStartDate, adjustedEndDate } = adjustDates(
      currentStartDate,
      currentEndDate,
      isVoid,
    );

    setStartDate(adjustedStartDate);
    setEndDate(adjustedEndDate);

    getContractTermSummary({
      variables: {
        contractId: editId,
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
      },
      onCompleted: data => {
        setSubscriptionData(data.getContractTermSummary);
      },
    });
  }, [
    isBatchCreate,
    editId,
    open,
    formValues.startDate,
    formValues.endDate,
    getContractTermSummary,
    isVoid,
  ]);

  useEffect(() => {
    if (!isBatchCreate && (error || clientError)) {
      setSnackbarState(
        'Unable to submit. Error getting associated subscription data',
        'error',
        5000,
      );
    }
  }, [error, clientError, setSnackbarState, isBatchCreate]);

  const hasNewContractPackages = formValues.contractPackages.some(cp => !cp.id);
  const isRetroactiveStartDate = dayjs(formValues.startDate).isBefore(
    dayjs(),
    'day',
  );
  const hasExistingSubmissions = (subscriptionData?.numSubmissions ?? 0) > 0;

  return (
    <HHDialog
      data-testid="contract-form-confirmation-dialog"
      open={open}
      onClose={(): void => onAction('cancel')}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
    >
      <HHDialogTitle data-testid="contract-form-confirmation-title">
        {getConfirmationTitle(isVoid, editId, hasExistingSubmissions)}
      </HHDialogTitle>
      <HHDialogContent data-testid="contract-form-confirmation-content">
        {loading ? (
          <HHCircularProgress />
        ) : (
          <>
            {hasExistingSubmissions ? (
              <HHTypography hhVariant="body2">
                {`This contract has ${subscriptionData?.numSubscriptions} subscriptions,
                  ${subscriptionData?.numFeeForServiceEvents} fee for service events,
                  ${subscriptionData?.numAdminFeeClaims} admin fee claims, and
                  ${subscriptionData?.numSubmissions} submissions totaling
                  ${formatCurrency(subscriptionData?.sumSubmissionAmount ?? 0, 'USD')}
                  that must be voided`
                  .replace(/\s+/g, ' ')
                  .trim()}
              </HHTypography>
            ) : (
              <NewContractPackageDetails
                notificationString={
                  isRetroactiveStartDate && !editId
                    ? 'Changes are retroactive and will trigger subscription generation'
                    : ''
                }
                formValues={formValues}
                availablePackages={availablePackages}
                billingModels={billingModels}
                hasNewContractPackages={hasNewContractPackages}
              />
            )}
          </>
        )}
      </HHDialogContent>
      <HHDialogActions>
        <HHButton
          data-testid="contract-form-confirmation-cancel-button"
          hhVariant="variant-bypass"
          variant="outlined"
          onClick={(): void => onAction('cancel')}
        >
          Cancel
        </HHButton>
        {hasExistingSubmissions ? (
          <HHButton
            data-testid="contract-form-confirmation-view-submissions-button"
            hhVariant="variant-bypass"
            variant="contained"
            color="primary"
            onClick={(e): void => {
              e.preventDefault();
              BillManagementNavigator(
                {
                  sourceStartDate: startDate,
                  sourceEndDate: endDate,
                  clientIds: clientId ? [clientId] : [],
                  insurerIds: insurerId ? [insurerId] : [],
                  submissionStatuses: submissionStatusesBlockingContractVoid,
                },
                navigate,
                GridLevelName.SUBMISSION,
                {
                  state: {
                    contractContext: {
                      returnUrl: `${routes.billing.home}${routes.billing.fullEditContractPackagePath.replace(':id', clientInsurerId.toString()).replace(':contractId', editId?.toString() ?? '')}`,
                      returnButtonText: 'Return to Contract',
                      endDate: formValues.endDate,
                    },
                  },
                },
              );
            }}
          >
            View Submissions
          </HHButton>
        ) : (
          <HHButton
            data-testid="contract-form-confirmation-confirm-button"
            hhVariant="variant-bypass"
            variant="contained"
            color={isVoid ? 'error' : 'primary'}
            onClick={(): void => onAction(isVoid ? 'void' : 'submit')}
            disabled={
              loading ||
              !!error ||
              (!isBatchCreate && (!!clientError || clientLoading))
            }
          >
            {confirmText}
          </HHButton>
        )}
      </HHDialogActions>
    </HHDialog>
  );
};
